import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '../hooks/useLocalizedPath';

interface LegalPageLayoutProps {
  title: string;
  lastUpdated?: string;
  children: ReactNode;
}

export default function LegalPageLayout({ title, lastUpdated, children }: LegalPageLayoutProps) {
  const { t } = useTranslation('common');
  const localizedPath = useLocalizedPath();

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950">
      {/* Animated background gradient orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-waffle-orange/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-96 h-96 bg-amber-500/20 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-96 h-96 bg-orange-600/15 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>

      {/* Sticky header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-gray-950/70 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <Link to={localizedPath('/')} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <img src="/logo.svg" alt="Family Waffle" className="w-8 h-8" />
            <span className="text-lg font-semibold text-white">Family Waffle</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 relative z-10 py-8 sm:py-12 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-3xl p-6 sm:p-10 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 shadow-2xl">
            <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-waffle-orange to-amber-500 bg-clip-text text-transparent mb-2">
              {title}
            </h1>
            {lastUpdated && (
              <p className="text-sm text-gray-400 mb-8">{t('legalLayout.lastUpdated', { date: lastUpdated })}</p>
            )}
            <div className="max-w-none text-gray-300 leading-relaxed
              [&_h2]:text-xl [&_h2]:sm:text-2xl [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:border-b [&_h2]:border-white/10 [&_h2]:pb-2 [&_h2]:text-white [&_h2]:font-semibold
              [&_h3]:text-lg [&_h3]:mt-6 [&_h3]:mb-3 [&_h3]:text-white [&_h3]:font-semibold
              [&_p]:mb-4 [&_p]:leading-relaxed
              [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ul]:space-y-2
              [&_li]:leading-relaxed
              [&_strong]:text-white [&_strong]:font-semibold
              [&_a]:text-waffle-orange [&_a]:no-underline hover:[&_a]:underline
            ">
              {children}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 bg-gray-950/50 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src="/logo.svg" alt="Family Waffle" className="w-6 h-6" />
              <span className="text-sm text-gray-400">{t('footer.copyright', { year: new Date().getFullYear() })}</span>
            </div>
            <nav className="flex items-center gap-6 text-sm">
              <Link to={localizedPath('/')} className="text-gray-400 hover:text-white transition-colors">{t('footer.home')}</Link>
              <Link to={localizedPath('/terms')} className="text-gray-400 hover:text-white transition-colors">{t('footer.terms')}</Link>
              <Link to={localizedPath('/privacy')} className="text-gray-400 hover:text-white transition-colors">{t('footer.privacy')}</Link>
              <Link to={localizedPath('/help')} className="text-gray-400 hover:text-white transition-colors">{t('footer.help')}</Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
